// components/Typography/Typography.tsx
import React from 'react';
import type { 
  TypographyProps, 
  HeadingProps, 
  BodyProps 
} from '../../assets/types/typography';
import styles from './style.module.css';

const Typography: React.FC<TypographyProps> = ({
  children,
  variant = 'bodyRegular',
  fontFamily = 'satoshi',
  weight = 'regular',
  size,
  align = 'left',
  lineHeight,
  letterSpacing,
  className = '',
  as: Component = 'p',
  ...props
}) => {
  // Build class names with type safety
  const classNames = [
    styles.text,
    fontFamily && styles[fontFamily],
    weight && styles[weight],
    variant && styles[variant],
    align && styles[`text${align.charAt(0).toUpperCase() + align.slice(1)}`],
    size && styles[size],
    lineHeight && styles[lineHeight],
    letterSpacing && styles[letterSpacing],
    className
  ].filter(Boolean).join(' ');

  return (
    <Component className={classNames} {...props}>
      {children}
    </Component>
  );
};

// Predefined component variants for common use cases
export const Heading1: React.FC<HeadingProps> = ({ 
  children, 
  className = '', 
  fontFamily,
  weight,
  align,
  ...props 
}) => (
  <Typography 
    as="h1" 
    variant="heading1" 
    fontFamily={fontFamily}
    weight={weight}
    align={align}
    className={className} 
    {...props}
  >
    {children}
  </Typography>
);

export const Heading2: React.FC<HeadingProps> = ({ 
  children, 
  className = '', 
  fontFamily,
  weight,
  align,
  ...props 
}) => (
  <Typography 
    as="h2" 
    variant="heading2" 
    fontFamily={fontFamily}
    weight={weight}
    align={align}
    className={className} 
    {...props}
  >
    {children}
  </Typography>
);

export const Heading3: React.FC<HeadingProps> = ({ 
  children, 
  className = '', 
  fontFamily,
  weight,
  align,
  ...props 
}) => (
  <Typography 
    as="h3" 
    variant="heading3" 
    fontFamily={fontFamily}
    weight={weight}
    align={align}
    className={className} 
    {...props}
  >
    {children}
  </Typography>
);

export const Heading4: React.FC<HeadingProps> = ({ 
  children, 
  className = '', 
  fontFamily,
  weight,
  align,
  ...props 
}) => (
  <Typography 
    as="h4" 
    variant="heading4" 
    fontFamily={fontFamily}
    weight={weight}
    align={align}
    className={className} 
    {...props}
  >
    {children}
  </Typography>
);

export const Heading5: React.FC<HeadingProps> = ({ 
  children, 
  className = '', 
  fontFamily,
  weight,
  align,
  ...props 
}) => (
  <Typography 
    as="h5" 
    variant="heading5" 
    fontFamily={fontFamily}
    weight={weight}
    align={align}
    className={className} 
    {...props}
  >
    {children}
  </Typography>
);

export const Heading6: React.FC<HeadingProps> = ({ 
  children, 
  className = '', 
  fontFamily,
  weight,
  align,
  ...props 
}) => (
  <Typography 
    as="h6" 
    variant="heading6" 
    fontFamily={fontFamily}
    weight={weight}
    align={align}
    className={className} 
    {...props}
  >
    {children}
  </Typography>
);

export const Body: React.FC<BodyProps> = ({ 
  children, 
  size = 'regular', 
  className = '', 
  fontFamily,
  weight,
  align,
  as = 'p',
  ...props 
}) => (
  <Typography 
    as={as}
    variant={`body${size.charAt(0).toUpperCase() + size.slice(1)}` as any}
    fontFamily={fontFamily}
    weight={weight}
    align={align}
    className={className} 
    {...props}
  >
    {children}
  </Typography>
);

export const Caption: React.FC<Omit<HeadingProps, 'children'> & { children: React.ReactNode }> = ({ 
  children, 
  className = '', 
  fontFamily,
  weight,
  align,
  ...props 
}) => (
  <Typography 
    as="span" 
    variant="caption" 
    fontFamily={fontFamily}
    weight={weight}
    align={align}
    className={className} 
    {...props}
  >
    {children}
  </Typography>
);

export const ButtonText: React.FC<Omit<HeadingProps, 'children'> & { children: React.ReactNode }> = ({ 
  children, 
  className = '', 
  fontFamily,
  weight,
  align,
  ...props 
}) => (
  <Typography 
    as="span" 
    variant="buttonText" 
    fontFamily={fontFamily}
    weight={weight}
    align={align}
    className={className} 
    {...props}
  >
    {children}
  </Typography>
);

export default Typography;